import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import ISO6391 from 'iso-639-1';
import { Country, State, City } from 'country-state-city';
import { formatDistanceToNow } from 'date-fns';
import {
    Plus, Download, Trash2, Lock, Check, Pencil, X, FileText,
    ChevronRight, MapPin, Mail, Sparkles, ExternalLink, Briefcase, GraduationCap,
    Eye, EyeOff,
} from 'lucide-react';

import Navbaar from '../../../components/Navbar/Navbaar';
import TutorialWizard from '../../../components/Onboarding/TutorialWizard';
import Sidebar from '../../../components/Navbar/SideNavbar';
import AddExpModal from '../../../components/Modals/AddExpModal';
import AddEduModal from '../../../components/Modals/AddEduModal';
import DeleteConfirmModal from '../../../components/Modals/DeleteConfirmModal';
import api from '../../../api/axios';

import {
    fetchUserProfile,
    updateUserProfile,
    deleteExperience,
    deleteEducation,
    logout,
    setHasPassword,
} from '../../../redux/AuthSlice/authSlice';
import { uploadResume } from '../../../redux/ResumeSlice/resumeSlice';
import { cancelSubscription } from '../../../redux/PaymentSlice/paymentSlice';
import { checkLinkedInConnectionStatus } from '../../../redux/LIAuthSlice/liAuthSlice';

// ─── static options ────────────────────────────────────────────────────────────

const languageOptions = ISO6391.getAllNames().map((n) => ({ value: n, label: n }));

const industryOptions = [
    'Technology', 'Healthcare', 'Education', 'Finance', 'Construction',
    'Food & Beverages', 'Design', 'Marketing', 'Legal', 'Consulting',
].map((v) => ({ value: v, label: v }));

const departmentOptions = [
    'C-Suite / Executive', 'Product', 'Engineering / Technical', 'IT & Security',
    'Design', 'Education', 'Finance', 'Human Resources', 'Legal', 'Marketing',
    'Medical & Health', 'Operations', 'Sales', 'Consulting',
].map((v) => ({ value: v, label: v }));

const departmentValueMap = {
    'C-Suite / Executive': 'c_suite',
    'Product': 'product_management',
    'Engineering / Technical': 'master_engineering_technical',
    'IT & Security': 'master_information_technology',
    'Design': 'design',
    'Education': 'education',
    'Finance': 'master_finance',
    'Human Resources': 'master_human_resources',
    'Legal': 'master_legal',
    'Marketing': 'master_marketing',
    'Medical & Health': 'medical_health',
    'Operations': 'master_operations',
    'Sales': 'master_sales',
    'Consulting': 'consulting',
};

const industryYearsOptions = Array.from({ length: 31 }, (_, i) => ({
    value: i, label: `${i} year${i !== 1 ? 's' : ''}`,
}));

// ─── helpers ───────────────────────────────────────────────────────────────────

const fmtBytes = (n) => {
    if (!n || n <= 0) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtMonthYear = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
};

const initials = (name = '') =>
    name.split(' ').filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');

function computeStrength(user, linkedInConnected) {
    const checks = [
        user?.name,
        user?.email,
        user?.contact,
        user?.languages?.length,
        user?.address?.country,
        user?.experience?.length,
        user?.education?.length,
        Array.isArray(user?.jobTitle) ? user.jobTitle.length : user?.jobTitle,
        user?.preferredIndustry?.length,
        user?.resumeUpload?.uploadedAt,
        linkedInConnected,
    ];
    const filled = checks.filter(Boolean).length;
    return Math.round((filled / checks.length) * 100);
}

function pwdStrength(pwd) {
    return [
        pwd.length >= 8,
        /[A-Z]/.test(pwd),
        /[0-9]/.test(pwd),
        /[^A-Za-z0-9]/.test(pwd),
    ];
}

function getRenewDate(currentPeriodEnd) {
    if (!currentPeriodEnd) return null;
    if (currentPeriodEnd.$date?.$numberLong)
        return new Date(parseInt(currentPeriodEnd.$date.$numberLong, 10));
    if (typeof currentPeriodEnd === 'string' || typeof currentPeriodEnd === 'number')
        return new Date(currentPeriodEnd);
    return null;
}

// ─── small shared pieces ────────────────────────────────────────────────────────

const Eyebrow = ({ children }) => (
    <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#94A3B8] font-dmsans">
        {children}
    </div>
);

const CardDivider = () => <div className="h-px bg-[#EFF1F5] my-2.5" />;

// Company/school logo tile (colored square with initial)
const LogoTile = ({ name = '', color = '#334155' }) => {
    const letter = (name || '?')[0].toUpperCase();
    return (
        <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[13px] font-bold font-dmsans"
            style={{ background: color }}
        >
            {letter}
        </div>
    );
};

// View-mode field (label + value)
const FieldRow = ({ label, value }) => (
    <div className="text-left">
        <Eyebrow>{label}</Eyebrow>
        <div className="text-[13px] font-medium text-[#0F172A] font-dmsans mt-1">{value || '—'}</div>
    </div>
);

// Chip list for matching prefs view mode
const ChipField = ({ label, chips = [], onAdd }) => (
    <div className="text-left">
        <Eyebrow>{label}</Eyebrow>
        <div className="flex flex-wrap gap-1.5 mt-2">
            {chips.map((c) => (
                <span key={c} className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold font-dmsans"
                    style={{ background: '#EEF1FE', color: '#3F57E0' }}>
                    {c}
                </span>
            ))}
            {chips.length === 0 && <span className="text-[12px] text-[#94A3B8] font-dmsans">Not set</span>}
        </div>
    </div>
);

// Card shell
const Card = ({ children, className = '', style = {} }) => (
    <div className={`rounded-xl border border-[#E6E8EE] bg-white ${className}`} style={style}>
        {children}
    </div>
);

// Card header row
const CardHead = ({ title, subtitle, action }) => (
    <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#EFF1F5]">
        <div className="text-left">
            <div className="text-[13.5px] font-bold text-[#0F172A] font-dmsans">{title}</div>
            {subtitle && <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-0.5">{subtitle}</div>}
        </div>
        {action}
    </div>
);

// Ghost-style small button
const BtnGhost = ({ children, onClick, className = '' }) => (
    <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[12px] font-medium font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors ${className}`}
    >
        {children}
    </button>
);

// Secondary small button
const BtnSec = ({ children, onClick, disabled, className = '', style = {} }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white text-[12px] font-semibold font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors disabled:opacity-50 ${className}`}
        style={style}
    >
        {children}
    </button>
);

// Primary small button
const BtnPrimary = ({ children, onClick, disabled, className = '', style = {} }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold font-dmsans text-white hover:opacity-90 transition-opacity disabled:opacity-50 ${className}`}
        style={{ backgroundColor: '#3F57E0', ...style }}
    >
        {children}
    </button>
);

// react-select shared styles
const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#3F57E0' : '#E6E8EE',
        backgroundColor: '#F8FAFC',
        borderRadius: '8px',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(63,87,224,0.12)' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#3F57E0' : '#CBD5E1' },
        minHeight: '36px',
        fontSize: '13px',
    }),
    multiValue: (base) => ({ ...base, backgroundColor: '#EEF1FE', borderRadius: '4px' }),
    multiValueLabel: (base) => ({ ...base, color: '#3F57E0', fontWeight: '500', fontSize: '12px' }),
    multiValueRemove: (base) => ({ ...base, color: '#3F57E0', '&:hover': { backgroundColor: '#C7C9F1', color: '#3F57E0' } }),
    placeholder: (base) => ({ ...base, color: '#94A3B8', fontSize: '13px' }),
    singleValue: (base) => ({ ...base, fontSize: '13px' }),
    option: (base, state) => ({ ...base, fontSize: '13px', backgroundColor: state.isFocused ? '#EEF1FE' : 'white', color: '#0F172A' }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const inputCls = 'w-full rounded-lg border border-[#E6E8EE] bg-[#F8FAFC] px-3 py-2 text-[12.5px] font-medium font-dmsans text-[#0F172A] outline-none placeholder:text-[#94A3B8] focus:border-[#3F57E0] focus:bg-white focus:ring-2 focus:ring-blue-100 transition-colors';
const labelCls = 'text-[11px] font-semibold text-[#64748B] font-dmsans mb-1 block';

// ─── skeleton block ──────────────────────────────────────────────────────────────
const Sk = ({ className = '' }) => (
    <div className={`animate-pulse bg-[#E9EBF0] rounded-lg ${className}`} />
);

// ─── main component ─────────────────────────────────────────────────────────────

const Profile = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((s) => s.auth.user);
    const { loading: resumeLoading } = useSelector((s) => s.resume);
    const isLinkedInConnected = useSelector((s) => s.linkedinAuth?.isLinkedInConnected);
    const isGoogleConnected = useSelector((s) => s.auth.isGoogleConnected);
    const hasPassword = useSelector((s) => s.auth.hasPassword ?? true);

    // When opened via "Connect Google to send" or the digest's "Reconnect"
    // (?connectGoogle=1), kick off the Google consent flow once the user is
    // loaded. Fires once. (Kept above the early return so hook order is stable.)
    //
    // DELIBERATELY NOT gated on `!isGoogleConnected`. RECONNECT IS A REQUEST
    // FROM AN ALREADY-CONNECTED USER: the Gmail "assumed" state means a refresh
    // token exists but its granted scopes were never recorded, so the account
    // reads as connected while nothing knows whether it may send. Skipping the
    // redirect for connected users made that the one state with no way out —
    // the digest's Reconnect link landed here and silently did nothing. The
    // backend forces `prompt: 'consent'` and records the scopes Google actually
    // grants, so re-running consent is exactly what resolves it.
    const autoConnectFired = useRef(false);
    useEffect(() => {
        if (autoConnectFired.current) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('connectGoogle') === '1' && user?.email) {
            autoConnectFired.current = true;
            const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
            window.open(`${baseURL}/api/auth/google?connectEmail=${encodeURIComponent(user.email)}&returnTo=${encodeURIComponent(window.location.origin)}`, '_self');
        }
    }, [user]);

    // ── product walkthrough ────────────────────────────────────────────────
    // Opened by the navbar's "Preview Tutorial wizard" via ?tutorial=true. The
    // param is stripped on open so a refresh or back-navigation does not silently
    // reopen the modal.
    const [showTutorial, setShowTutorial] = useState(false);
    const location = useLocation();
    useEffect(() => {
        // Must read from useLocation, not window.location. Clicking the navbar
        // button is a client-side navigate: the URL changes but this component
        // never remounts, so an effect keyed on window.location would only fire
        // on a hard refresh — which is exactly how this broke the first time.
        const params = new URLSearchParams(location.search);
        if (params.get('tutorial') === 'true') {
            setShowTutorial(true);
            navigate('/profile', { replace: true });
        }
    }, [location.search, navigate]);

    // ── exp/edu modals ─────────────────────────────────────────────────────
    const [expOpen, setExpOpen] = useState(false);
    const [expEditIdx, setExpEditIdx] = useState(null);
    const [expEditData, setExpEditData] = useState(null);

    const [eduOpen, setEduOpen] = useState(false);
    const [eduEditIdx, setEduEditIdx] = useState(null);
    const [eduEditData, setEduEditData] = useState(null);

    // ── view-only modals ───────────────────────────────────────────────────
    const [viewExp, setViewExp] = useState(null);
    const [viewEdu, setViewEdu] = useState(null);

    // ── logout confirm ─────────────────────────────────────────────────────
    const [logoutConfirm, setLogoutConfirm] = useState(false);

    // ── delete confirm ─────────────────────────────────────────────────────
    const [delOpen, setDelOpen] = useState(false);
    const [delTarget, setDelTarget] = useState(null);

    // ── contact ────────────────────────────────────────────────────────────
    const [contactEdit, setContactEdit] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [langs, setLangs] = useState([]);
    const [addr, setAddr] = useState({ country: '', state: '', city: '', zip: '', fullAddress: '' });
    const [countryOpts, setCountryOpts] = useState([]);
    const [stateOpts, setStateOpts] = useState([]);
    const [cityOpts, setCityOpts] = useState([]);
    const [contactSaving, setContactSaving] = useState(false);
    const [contactMsg, setContactMsg] = useState('');

    // ── matching prefs ─────────────────────────────────────────────────────
    const [prefEdit, setPrefEdit] = useState(false);
    const [jobTitles, setJobTitles] = useState([]);
    const [prefIndustries, setPrefIndustries] = useState([]);
    const [prefDept, setPrefDept] = useState(null);
    // Work mode is multi-select; stored as a comma-separated string in remotePreference.
    const [workModes, setWorkModes] = useState([]);
    const WORK_MODE_OPTIONS = ['Remote', 'Hybrid', 'On-site'];
    const toggleWorkMode = (value) => {
        setWorkModes((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
    };
    const [prevIndustryYears, setPrevIndustryYears] = useState([{ industry: null, years: null }]);
    const [customIndustryOpts, setCustomIndustryOpts] = useState(industryOptions);
    const [prefSaving, setPrefSaving] = useState(false);
    const [prefMsg, setPrefMsg] = useState('');

    // ── resume ─────────────────────────────────────────────────────────────
    const resumeRef = useRef(null);
    const [resumeMsg, setResumeMsg] = useState('');

    // ── toast ──────────────────────────────────────────────────────────────
    const [toast, setToast] = useState({ msg: '', type: 'default' });
    const showToast = (msg, type = 'default') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: 'default' }), 2500); };

    // ── password ───────────────────────────────────────────────────────────
    const [pwdOpen, setPwdOpen] = useState(false);
    const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
    const [pwdSaving, setPwdSaving] = useState(false);
    const [pwdMsg, setPwdMsg] = useState({ text: '', ok: false });
    const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
    const [signOutAll, setSignOutAll] = useState(true);

    // ── set-password (OAuth users) ─────────────────────────────────────────
    const [setpwdOpen, setSetpwdOpen] = useState(false);
    const [setpwd, setSetpwd] = useState({ next: '', confirm: '' });
    const [setpwdSaving, setSetpwdSaving] = useState(false);
    const [setpwdMsg, setSetpwdMsg] = useState({ text: '', ok: false });
    const [showSetpwd, setShowSetpwd] = useState({ next: false, confirm: false });

    // ── fetch ──────────────────────────────────────────────────────────────
    const [profileLoading, setProfileLoading] = useState(true);

    useEffect(() => {
        dispatch(fetchUserProfile()).finally(() => setProfileLoading(false));
        dispatch(checkLinkedInConnectionStatus());
    }, [dispatch]);

    useEffect(() => {
        setCountryOpts(Country.getAllCountries().map((c) => ({ label: c.name, value: c.isoCode })));
    }, []);

    useEffect(() => {
        if (!user) return;
        setName(user.name || '');
        setPhone(user.contact || '');
        setLangs((user.languages || []).map((l) => ({ value: l, label: l })));
        const a = user.address || {};
        setAddr({ country: a.country || '', state: a.state || '', city: a.city || '', zip: a.zip || '', fullAddress: a.fullAddress || '' });
        if (a.country) setStateOpts(State.getStatesOfCountry(a.country).map((s) => ({ label: s.name, value: s.isoCode })));
        if (a.country && a.state) setCityOpts(City.getCitiesOfState(a.country, a.state).map((c) => ({ label: c.name, value: c.name })));
    }, [user]);

    useEffect(() => {
        if (!user) return;
        setJobTitles((Array.isArray(user.jobTitle) ? user.jobTitle : user.jobTitle ? [user.jobTitle] : []).map((t) => ({ label: t, value: t })));
        setPrefIndustries((Array.isArray(user.preferredIndustry) ? user.preferredIndustry : []).map((i) => ({ label: i, value: i })));
        setPrefDept(user.preferredDepartment?.length ? { label: user.preferredDepartment[0], value: user.preferredDepartment[0] } : null);
        setWorkModes(
            String(user.remotePreference || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                // Normalize casing to the canonical option labels (e.g. "remote" -> "Remote").
                .map((m) => WORK_MODE_OPTIONS.find((o) => o.toLowerCase() === m.toLowerCase()) || m)
        );
        setPrevIndustryYears(
            user.previousIndustryYears?.length
                ? user.previousIndustryYears.map((e) => ({
                    industry: e.previousIndustry ? { label: e.previousIndustry, value: e.previousIndustry } : null,
                    years: e.experienceYears != null ? { label: `${e.experienceYears} year${e.experienceYears !== 1 ? 's' : ''}`, value: e.experienceYears } : null,
                }))
                : [{ industry: null, years: null }]
        );
    }, [user]);

    if (profileLoading || !user) return (
        <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#F6F7FB' }}>
            <div className="fixed top-0 left-0 right-0 z-50"><Navbaar /></div>
            <div className="flex flex-1 min-h-0 pt-16">
                <div className="hidden sm:flex flex-col w-64 flex-shrink-0 fixed top-16 bottom-0 left-0 z-40 border-r border-[#E6E8EE] bg-white"><Sidebar /></div>
                <div className="flex-1 sm:ml-64 overflow-y-auto">
                    <div className="px-4 sm:px-5 py-5">
                        <div className="flex gap-[18px] items-start">
                            {/* left column */}
                            <div className="flex-1 min-w-0 flex flex-col gap-[14px]">
                                {/* identity */}
                                <div className="rounded-xl border border-[#E6E8EE] bg-white p-5">
                                    <div className="flex gap-4 items-center">
                                        <Sk className="w-16 h-16 rounded-full flex-shrink-0" />
                                        <div className="flex-1 flex flex-col gap-2">
                                            <Sk className="h-6 w-48" />
                                            <Sk className="h-4 w-72" />
                                            <Sk className="h-3 w-36" />
                                        </div>
                                        <Sk className="hidden sm:block w-[200px] h-[88px] rounded-xl flex-shrink-0" />
                                    </div>
                                </div>
                                {/* work exp */}
                                <div className="rounded-xl border border-[#E6E8EE] bg-white">
                                    <div className="px-5 py-4 border-b border-[#EFF1F5] flex items-center justify-between">
                                        <Sk className="h-4 w-32" /><Sk className="h-7 w-12" />
                                    </div>
                                    <div className="px-5 py-4 flex flex-col gap-4">
                                        {[1,2].map(i => (
                                            <div key={i} className="flex gap-3 items-center">
                                                <Sk className="w-9 h-9 rounded-lg flex-shrink-0" />
                                                <div className="flex-1 flex flex-col gap-1.5">
                                                    <Sk className="h-3.5 w-40" /><Sk className="h-3 w-56" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* education */}
                                <div className="rounded-xl border border-[#E6E8EE] bg-white">
                                    <div className="px-5 py-4 border-b border-[#EFF1F5] flex items-center justify-between">
                                        <Sk className="h-4 w-36" /><Sk className="h-7 w-12" />
                                    </div>
                                    <div className="px-5 py-4 flex gap-3 items-center">
                                        <Sk className="w-9 h-9 rounded-lg flex-shrink-0" />
                                        <div className="flex-1 flex flex-col gap-1.5">
                                            <Sk className="h-3.5 w-48" /><Sk className="h-3 w-40" />
                                        </div>
                                    </div>
                                </div>
                                {/* contact */}
                                <div className="rounded-xl border border-[#E6E8EE] bg-white">
                                    <div className="px-5 py-4 border-b border-[#EFF1F5] flex items-center justify-between">
                                        <Sk className="h-4 w-20" /><Sk className="h-7 w-10" />
                                    </div>
                                    <div className="px-5 py-4 grid grid-cols-2 gap-4">
                                        {[1,2,3,4,5,6].map(i => (
                                            <div key={i} className="flex flex-col gap-1.5">
                                                <Sk className="h-2.5 w-14" /><Sk className="h-4 w-32" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {/* right sidebar */}
                            <div className="hidden lg:flex flex-col gap-[14px] w-[300px] flex-shrink-0">
                                <div className="rounded-xl border border-[#E6E8EE] bg-white p-4">
                                    <Sk className="h-4 w-24 mb-4" />
                                    <Sk className="h-28 w-full rounded-xl" />
                                    <div className="grid grid-cols-2 gap-1.5 mt-3">
                                        <Sk className="h-8" /><Sk className="h-8" />
                                    </div>
                                </div>
                                <div className="rounded-xl border border-[#E6E8EE] bg-white p-4">
                                    <Sk className="h-3 w-32 mb-4" />
                                    {[1,2,3].map(i => (
                                        <div key={i} className="flex items-center gap-2.5 py-2">
                                            <Sk className="w-8 h-8 rounded-lg flex-shrink-0" />
                                            <div className="flex-1 flex flex-col gap-1"><Sk className="h-3 w-20" /><Sk className="h-2.5 w-28" /></div>
                                            <Sk className="h-6 w-16 rounded-full" />
                                        </div>
                                    ))}
                                </div>
                                <div className="rounded-xl border border-[#E6E8EE] bg-white overflow-hidden">
                                    <Sk className="h-20 w-full rounded-none" />
                                    <div className="p-4 flex flex-col gap-3">
                                        <Sk className="h-10 w-full" /><Sk className="h-8 w-full" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // ── derived ────────────────────────────────────────────────────────────
    const linkedInConnected = Array.isArray(isLinkedInConnected) ? isLinkedInConnected.length > 0 : !!isLinkedInConnected;
    const strength = computeStrength(user, linkedInConnected);
    const strengthColor = strength >= 70 ? '#10B981' : strength >= 40 ? '#F59E0B' : '#EF4444';
    const strengthLabel = strength >= 70 ? 'Strong' : strength >= 40 ? 'Good' : 'Incomplete';
    const strengthTip = (() => {
        if (!user.name)                       return 'Add your full name to reach 100%';
        if (!user.contact)                    return 'Add your phone number to reach 100%';
        if (!user.languages?.length)          return 'Add languages to reach 100%';
        if (!user.address?.country)           return 'Add your country to reach 100%';
        if (!user.experience?.length)         return 'Add work experience to reach 100%';
        if (!user.education?.length)          return 'Add education to reach 100%';
        if (!(Array.isArray(user.jobTitle) ? user.jobTitle.length : user.jobTitle)) return 'Add a target job title to reach 100%';
        if (!user.preferredIndustry?.length)  return 'Add preferred industries to reach 100%';
        if (!user.resumeUpload?.uploadedAt)   return 'Upload your CV to reach 100%';
        if (!linkedInConnected)               return 'Connect your LinkedIn account to reach 100%';
        return null;
    })();

    const renewDate = getRenewDate(user.subscription?.currentPeriodEnd);
    const renewTime = renewDate ? formatDistanceToNow(renewDate, { addSuffix: true }) : null;
    const renewDateStr = renewDate ? renewDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : null;
    const isFreeTier = (user.subscription?.tier || 'free') === 'free';
    const trialEnd = user.trialEndsAt ? new Date(user.trialEndsAt) : null;
    const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / 86400000)) : 0;
    const trialExpired = trialEnd ? trialEnd.getTime() <= Date.now() : false;

    const pwdBars = pwdStrength(pwd.next);
    const pwdScore = pwdBars.filter(Boolean).length;
    const pwdStrengthColor = pwdScore <= 1 ? '#F97066' : pwdScore === 2 ? '#FDB022' : pwdScore === 3 ? '#3F57E0' : '#10B981';

    const googleConnected = isGoogleConnected;

    // Connect (or reconnect) the user's Google account so the platform can send
    // email as them via the Gmail API (grants the gmail.send scope + a refresh
    // token). Returns to /oauth-callback, which re-establishes the session.
    const handleConnectGoogle = () => {
        const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
        // Pass the logged-in email so the backend rejects connecting a different Google account.
        const q = user?.email ? `?connectEmail=${encodeURIComponent(user.email)}` : '';
        const sep = q ? '&' : '?';
        window.open(`${baseURL}/api/auth/google${q}${sep}returnTo=${encodeURIComponent(window.location.origin)}`, '_self');
    };

    // ── handlers ───────────────────────────────────────────────────────────
    const handleSaveContact = async () => {
        setContactSaving(true);
        setContactMsg('');
        try {
            await dispatch(updateUserProfile({ name, contact: phone, languages: langs.map((l) => l.value), address: addr })).unwrap();
            setContactEdit(false);
            setContactMsg('Saved.');
            setTimeout(() => setContactMsg(''), 3000);
        } catch { setContactMsg('Failed to save.'); }
        finally { setContactSaving(false); }
    };

    const handleSavePrefs = async () => {
        setPrefSaving(true); setPrefMsg('');
        try {
            await dispatch(updateUserProfile({
                jobTitle: jobTitles.map((t) => t.value),
                preferredIndustry: prefIndustries.map((i) => i.value),
                industryPreference: prefIndustries.map((i) => i.value),
                preferredDepartment: prefDept
                    ? [prefDept.label, departmentValueMap[prefDept.label] || prefDept.label].filter((v, i, a) => a.indexOf(v) === i)
                    : [],
                remotePreference: workModes.join(', '),
                previousIndustryYears: prevIndustryYears.map((e) => ({ previousIndustry: e.industry?.value, experienceYears: e.years?.value })),
            })).unwrap();
            setPrefEdit(false); setPrefMsg('Saved.'); setTimeout(() => setPrefMsg(''), 3000);
        } catch { setPrefMsg('Failed to save.'); }
        finally { setPrefSaving(false); }
    };

    const handleResumeUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowed.includes(file.type)) { setResumeMsg('Please upload a PDF or Word document.'); e.target.value = ''; return; }
        try {
            setResumeMsg('Uploading…');
            await dispatch(uploadResume(file)).unwrap();
            await dispatch(fetchUserProfile());
            setResumeMsg('');
        } catch { setResumeMsg('Upload failed. Please try again.'); }
        finally { e.target.value = ''; }
    };

    const handleDownloadResume = async () => {
        try {
            setResumeMsg('Preparing…');
            const res = await api.get('/api/resume/current', { responseType: 'blob' });
            const cd = res.headers?.['content-disposition'] || '';
            const match = cd.match(/filename="?([^";]+)"?/i);
            const fileName = match?.[1] || user?.resumeUpload?.fileName || 'careerchamp-cv';
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a'); a.href = url; a.download = fileName;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url); setResumeMsg('');
        } catch { setResumeMsg('Download failed.'); }
    };

    const handleDeleteResume = async () => {
        try {
            await api.delete('/api/resume/current');
            await dispatch(fetchUserProfile()); setDelOpen(false); setDelTarget(null);
        } catch { setDelOpen(false); setDelTarget(null); }
    };

    const handleChangePassword = async () => {
        if (pwd.next !== pwd.confirm) { setPwdMsg({ text: 'Passwords do not match.', ok: false }); return; }
        if (pwd.next.length < 8) { setPwdMsg({ text: 'New password must be at least 8 characters.', ok: false }); return; }
        if (pwdScore < 1) { setPwdMsg({ text: 'Password is too weak.', ok: false }); return; }
        setPwdSaving(true); setPwdMsg({ text: '', ok: false });
        let succeeded = false;
        try {
            await api.post('/api/auth/change-password', { currentPassword: pwd.current, newPassword: pwd.next, signOutAllDevices: signOutAll });
            succeeded = true;
            setPwd({ current: '', next: '', confirm: '' });
            setPwdMsg({ text: '', ok: false });
            setPwdOpen(false);
        } catch (err) { setPwdMsg({ text: err?.response?.data?.message || 'Failed to update password.', ok: false }); }
        finally { setPwdSaving(false); }
        if (succeeded) showToast('Password changed successfully.', 'success');
    };

    const handleSetPassword = async () => {
        if (setpwd.next !== setpwd.confirm) { setSetpwdMsg({ text: 'Passwords do not match.', ok: false }); return; }
        if (setpwd.next.length < 8) { setSetpwdMsg({ text: 'Password must be at least 8 characters.', ok: false }); return; }
        const bars = pwdStrength(setpwd.next);
        if (bars.filter(Boolean).length < 1) { setSetpwdMsg({ text: 'Password is too weak.', ok: false }); return; }
        setSetpwdSaving(true); setSetpwdMsg({ text: '', ok: false });
        let succeeded = false;
        try {
            await api.post('/api/auth/set-password', { newPassword: setpwd.next });
            succeeded = true;
            setSetpwd({ next: '', confirm: '' });
            setSetpwdMsg({ text: '', ok: false });
            setSetpwdOpen(false);
            dispatch(setHasPassword(true));
        } catch (err) { setSetpwdMsg({ text: err?.response?.data?.message || 'Failed to set password.', ok: false }); }
        finally { setSetpwdSaving(false); }
        if (succeeded) showToast('Password set successfully. You can now sign in with email & password.', 'success');
    };

    const handleCancelSub = async () => {
        try {
            await dispatch(cancelSubscription()).unwrap();
            await dispatch(fetchUserProfile());
        } finally { setDelOpen(false); setDelTarget(null); }
    };

    const confirmDelete = () => {
        if (!delTarget) return;
        if (delTarget.type === 'exp') { dispatch(deleteExperience(delTarget.index)); setDelOpen(false); setDelTarget(null); }
        if (delTarget.type === 'edu') { dispatch(deleteEducation(delTarget.index)); setDelOpen(false); setDelTarget(null); }
        if (delTarget.type === 'resume') handleDeleteResume();
        if (delTarget.type === 'sub') handleCancelSub();
    };

    // ── experience / education colors (deterministic from name) ──────────
    const COMPANY_COLORS = ['#0F172A', '#1F2937', '#1D4ED8', '#065F46', '#7C3AED', '#B45309', '#BE123C', '#0369A1'];
    const colorFor = (name = '') => COMPANY_COLORS[name.charCodeAt(0) % COMPANY_COLORS.length];

    // ── address display string ─────────────────────────────────────────────
    const addrDisplay = [user.address?.city, user.address?.state, user.address?.country].filter(Boolean).join(', ');

    // ─── render ────────────────────────────────────────────────────────────
    return (
        <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#F6F7FB' }}>
            {showTutorial && (
                <TutorialWizard
                    onClose={() => setShowTutorial(false)}
                    onFinish={() => setShowTutorial(false)}
                />
            )}
            <div className="fixed top-0 left-0 right-0 z-50">
                <Navbaar />
            </div>

            <div className="flex flex-1 min-h-0 pt-16">
                {/* fixed sidebar */}
                <div className="hidden sm:flex flex-col w-64 flex-shrink-0 fixed top-16 bottom-0 left-0 z-40 border-r border-[#E6E8EE] bg-white">
                    <Sidebar />
                </div>

                {/* scrollable content */}
                <div className="flex-1 sm:ml-64 overflow-y-auto">
                <div className="px-4 sm:px-5 py-5">
                    <div className="flex gap-[18px] items-start">

                        {/* ══ LEFT COLUMN ══════════════════════════════════════════ */}
                        <div className="flex-1 min-w-0 flex flex-col gap-[14px]">

                            {/* ── Identity card ───────────────────────────────── */}
                            <Card className="p-5">
                                <div className="flex gap-4 items-center">
                                    {/* avatar */}
                                    {user.photo ? (
                                        <img
                                            src={user.photo}
                                            alt={user.name || ''}
                                            className="w-16 h-16 rounded-full flex-shrink-0 object-cover"
                                            referrerPolicy="no-referrer"
                                        />
                                    ) : (
                                        <div
                                            className="w-16 h-16 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xl font-bold font-dmsans"
                                            style={{ background: 'linear-gradient(135deg, #3F57E0, #8B5CF6)' }}
                                        >
                                            {initials(user.name) || (user.email?.[0] || '?').toUpperCase()}
                                        </div>
                                    )}

                                    {/* name + meta */}
                                    <div className="flex-1 min-w-0 text-left">
                                        <div className="text-[20px] font-bold text-[#0F172A] font-dmsans leading-tight truncate">
                                            {user.name || (user.email ? user.email.split('@')[0] : 'My Account')}
                                        </div>
                                        <div className="text-[13px] font-medium text-[#334155] font-dmsans mt-0.5">
                                            {[
                                                Array.isArray(user.jobTitle) && user.jobTitle.length ? user.jobTitle[0] : null,
                                                user.preferredIndustry?.length ? user.preferredIndustry.slice(0, 2).join(' & ') : null,
                                            ].filter(Boolean).join(' · ') || 'Career Champ member'}
                                        </div>
                                        <div className="flex gap-3 mt-2 flex-wrap">
                                            {addrDisplay && (
                                                <span className="text-[11.5px] text-[#64748B] font-dmsans flex items-center gap-1">
                                                    <MapPin size={11} className="flex-shrink-0" /> {addrDisplay}
                                                </span>
                                            )}
                                            <span className="text-[11.5px] text-[#64748B] font-dmsans flex items-center gap-1">
                                                <Mail size={11} className="flex-shrink-0" /> {user.email}
                                            </span>
                                        </div>
                                    </div>

                                    {/* profile strength mini-card */}
                                    <div className="flex-shrink-0 rounded-xl border border-[#E6E8EE] bg-[#FAFBFD] p-3 text-center min-w-[200px] hidden sm:block">
                                        <Eyebrow>Profile strength</Eyebrow>
                                        <div className="flex items-center justify-center gap-2 mt-1">
                                            <div className="text-[22px] font-extrabold font-dmsans" style={{ color: strengthColor }}>{strength}%</div>
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: strengthColor + '1A', color: strengthColor }}>{strengthLabel}</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-[#EEF1F5] overflow-hidden mt-2">
                                            <div className="h-full rounded-full" style={{ width: `${strength}%`, background: strengthColor }} />
                                        </div>
                                        {strengthTip && (
                                            <div className="mt-2 text-[10.5px] font-medium text-left leading-snug text-[#64748B]">
                                                💡 {strengthTip}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>

                            {/* ── Work Experience ──────────────────────────────── */}
                            <Card>
                                <CardHead
                                    title="Work experience"
                                    action={
                                        <BtnGhost onClick={() => { setExpEditIdx(null); setExpEditData(null); setExpOpen(true); }}>
                                            <Plus size={11} /> Add
                                        </BtnGhost>
                                    }
                                />
                                <div className="px-5 pt-1 pb-4">
                                    {user.experience?.length ? user.experience.map((exp, idx) => (
                                        <div
                                            key={idx}
                                            className="flex gap-3 py-3 items-center hover:bg-[#FAFBFD] -mx-2 px-2 rounded-lg transition-colors"
                                            style={{ borderBottom: idx < user.experience.length - 1 ? '1px solid #EFF1F5' : 'none' }}
                                        >
                                            <LogoTile name={exp.company} color={colorFor(exp.company)} />
                                            <div className="flex-1 min-w-0 text-left">
                                                <div className="text-[13px] font-bold text-[#0F172A] font-dmsans truncate">{exp.title}</div>
                                                <div className="text-[11.5px] text-[#64748B] font-dmsans mt-0.5">
                                                    {exp.company} · {fmtMonthYear(exp.startingDate)} – {exp.stillWorking ? 'Present' : fmtMonthYear(exp.endingDate)}
                                                </div>
                                                {exp.description && (
                                                    <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-1 leading-relaxed">
                                                        {exp.description.length > 100 ? exp.description.slice(0, 100) + '...' : exp.description}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                                                <button type="button" onClick={() => setViewExp(exp)} className="text-[11.5px] text-[#64748B] font-dmsans hover:text-[#3F57E0] transition-colors">View details</button>
                                                <span className="text-[#D1D5DB]">·</span>
                                                <button type="button" onClick={() => { setExpEditIdx(idx); setExpEditData(exp); setExpOpen(true); }} className="text-[11.5px] text-[#64748B] font-dmsans hover:text-[#3F57E0] transition-colors">Edit details</button>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="rounded-lg border border-dashed border-[#E6E8EE] p-5 text-center mt-2">
                                            <p className="text-[13px] font-semibold text-[#334155] font-dmsans">No experience added yet</p>
                                            <p className="text-[12px] text-[#94A3B8] font-dmsans mt-1">Add roles to improve job-fit matching</p>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* ── Education ────────────────────────────────────── */}
                            <Card>
                                <CardHead
                                    title="Education experience"
                                    action={
                                        <BtnGhost onClick={() => { setEduEditIdx(null); setEduEditData(null); setEduOpen(true); }}>
                                            <Plus size={11} /> Add
                                        </BtnGhost>
                                    }
                                />
                                <div className="px-5 pt-1 pb-4">
                                    {user.education?.length ? user.education.map((edu, idx) => (
                                        <div
                                            key={idx}
                                            className="flex gap-3 py-3 items-center hover:bg-[#FAFBFD] -mx-2 px-2 rounded-lg transition-colors"
                                            style={{ borderBottom: idx < user.education.length - 1 ? '1px solid #EFF1F5' : 'none' }}
                                        >
                                            <LogoTile name={edu.school} color={colorFor(edu.school)} />
                                            <div className="flex-1 min-w-0 text-left">
                                                <div className="text-[13px] font-bold text-[#0F172A] font-dmsans truncate">{edu.degree}{edu.field ? ` · ${edu.field}` : ''}</div>
                                                <div className="text-[11.5px] text-[#64748B] font-dmsans mt-0.5">
                                                    {edu.school} · {edu.startingYear} – {edu.endingYear}
                                                </div>
                                            </div>
                                            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                                                <button type="button" onClick={() => setViewEdu(edu)} className="text-[11.5px] text-[#64748B] font-dmsans hover:text-[#3F57E0] transition-colors">View details</button>
                                                <span className="text-[#D1D5DB]">·</span>
                                                <button type="button" onClick={() => { setEduEditIdx(idx); setEduEditData(edu); setEduOpen(true); }} className="text-[11.5px] text-[#64748B] font-dmsans hover:text-[#3F57E0] transition-colors">Edit details</button>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="rounded-lg border border-dashed border-[#E6E8EE] p-5 text-center mt-2">
                                            <p className="text-[13px] font-semibold text-[#334155] font-dmsans">No education added yet</p>
                                            <p className="text-[12px] text-[#94A3B8] font-dmsans mt-1">Add degrees to improve profile completeness</p>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* ── Contact ──────────────────────────────────────── */}
                            <Card>
                                <CardHead
                                    title="Contact"
                                    action={
                                        contactEdit
                                            ? (
                                                <div className="flex items-center gap-2">
                                                    <BtnGhost onClick={() => setContactEdit(false)}>Cancel</BtnGhost>
                                                    <BtnPrimary onClick={handleSaveContact} disabled={contactSaving}>
                                                        {contactSaving ? 'Saving…' : 'Save'}
                                                    </BtnPrimary>
                                                </div>
                                            )
                                            : <BtnGhost onClick={() => setContactEdit(true)}>Edit</BtnGhost>
                                    }
                                />
                                {contactMsg && (
                                    <div className="px-5 pt-3">
                                        <p className="text-[12px] font-medium font-dmsans text-[#10B981]">{contactMsg}</p>
                                    </div>
                                )}
                                {contactEdit ? (
                                    <div className="px-5 py-4 flex flex-col gap-3">
                                        <div>
                                            <label className={labelCls}>Full name</label>
                                            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className={labelCls}>Phone</label>
                                                <input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Languages</label>
                                                <CreatableSelect isMulti options={languageOptions} value={langs} onChange={setLangs} styles={selectStyles} placeholder="Select languages…" menuPortalTarget={document.body} menuPosition="fixed" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <div>
                                                <label className={labelCls}>Country</label>
                                                <CreatableSelect options={countryOpts} value={addr.country ? countryOpts.find((c) => c.value === addr.country) || null : null} onChange={(v) => { setAddr({ ...addr, country: v.value, state: '', city: '' }); setStateOpts(State.getStatesOfCountry(v.value).map((s) => ({ label: s.name, value: s.isoCode }))); setCityOpts([]); }} styles={selectStyles} placeholder="Country" menuPortalTarget={document.body} menuPosition="fixed" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>State</label>
                                                <CreatableSelect options={stateOpts} value={addr.state ? stateOpts.find((s) => s.value === addr.state) || null : null} onChange={(v) => { setAddr({ ...addr, state: v.value, city: '' }); setCityOpts(City.getCitiesOfState(addr.country, v.value).map((c) => ({ label: c.name, value: c.name }))); }} isDisabled={!addr.country} styles={selectStyles} placeholder="State" menuPortalTarget={document.body} menuPosition="fixed" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>City</label>
                                                <CreatableSelect options={cityOpts} value={addr.city ? { label: addr.city, value: addr.city } : null} onChange={(v) => setAddr({ ...addr, city: v.value })} isDisabled={!addr.state} styles={selectStyles} placeholder="City" menuPortalTarget={document.body} menuPosition="fixed" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-[100px_1fr] gap-3">
                                            <div>
                                                <label className={labelCls}>Zip code</label>
                                                <input className={inputCls} value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} placeholder="Zip" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Full address</label>
                                                <input className={inputCls} value={addr.fullAddress} onChange={(e) => setAddr({ ...addr, fullAddress: e.target.value })} placeholder="Street, apt…" />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FieldRow label="Full name" value={user.name} />
                                        <FieldRow label="Email" value={user.email} />
                                        <FieldRow label="Phone" value={user.contact} />
                                        <FieldRow label="Languages" value={(user.languages || []).join(' · ')} />
                                        <FieldRow label="Country" value={countryOpts.find((c) => c.value === user.address?.country)?.label || user.address?.country} />
                                        <FieldRow label="State" value={stateOpts.find((s) => s.value === user.address?.state)?.label || user.address?.state} />
                                        <FieldRow label="City" value={user.address?.city} />
                                        <FieldRow label="Zip code" value={user.address?.zip} />
                                        <FieldRow label="Full address" value={user.address?.fullAddress} />
                                    </div>
                                )}
                            </Card>

                            {/* ── Matching Preferences ─────────────────────────── */}
                            <Card>
                                <CardHead
                                    title="Matching preferences"
                                    subtitle="Job titles, industries, and departments used to personalize matches."
                                    action={
                                        prefEdit
                                            ? (
                                                <div className="flex items-center gap-2">
                                                    <BtnGhost onClick={() => setPrefEdit(false)}>Cancel</BtnGhost>
                                                    <BtnPrimary onClick={handleSavePrefs} disabled={prefSaving}>
                                                        {prefSaving ? 'Saving…' : 'Save'}
                                                    </BtnPrimary>
                                                </div>
                                            )
                                            : <BtnGhost onClick={() => setPrefEdit(true)}>Edit</BtnGhost>
                                    }
                                />
                                {prefMsg && <div className="px-5 pt-3"><p className="text-[12px] font-medium font-dmsans text-[#10B981]">{prefMsg}</p></div>}
                                {prefEdit ? (
                                    <div className="px-5 py-4 flex flex-col gap-3">
                                        <div>
                                            <label className={labelCls}>Target job titles</label>
                                            <CreatableSelect isMulti components={{ DropdownIndicator: null }} value={jobTitles} onChange={(v) => { if ((v || []).length <= 5) setJobTitles(v || []); }} placeholder="Type a title and press enter…" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className={labelCls}>Preferred industries</label>
                                                <CreatableSelect isMulti options={customIndustryOpts} value={prefIndustries} onChange={(v) => setPrefIndustries(v || [])} onCreateOption={(val) => { const opt = { label: val, value: val }; setCustomIndustryOpts((p) => [...p, opt]); setPrefIndustries((p) => [...p, opt]); }} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Preferred department</label>
                                                <CreatableSelect options={departmentOptions} value={prefDept} onChange={setPrefDept} placeholder="Select department…" isClearable styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelCls}>Work mode</label>
                                            <div className="flex gap-2">
                                                {WORK_MODE_OPTIONS.map((mode) => (
                                                    <button
                                                        key={mode}
                                                        type="button"
                                                        onClick={() => toggleWorkMode(mode)}
                                                        aria-pressed={workModes.includes(mode)}
                                                        className={`flex-1 py-2 rounded-lg text-[12px] font-medium font-dmsans border transition-colors ${
                                                            workModes.includes(mode)
                                                                ? 'border-[#3F57E0] bg-[#EEF1FE] text-[#3F57E0] font-semibold'
                                                                : 'border-[#E6E8EE] text-[#334155] hover:bg-[#F4F5F8]'
                                                        }`}
                                                    >
                                                        {mode}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelCls}>Experience context</label>
                                            <p className="text-[10.5px] text-[#94A3B8] font-dmsans mb-2">Your background industry and years of experience</p>
                                            {prevIndustryYears.map((entry, idx) => (
                                                <div key={idx} className="grid grid-cols-2 gap-3 mt-1">
                                                    <div>
                                                        <label className="text-[10px] font-medium text-[#94A3B8] font-dmsans mb-0.5 block">Previous industry</label>
                                                        <CreatableSelect options={customIndustryOpts} value={entry.industry} onChange={(v) => { const u = [...prevIndustryYears]; u[idx] = { ...u[idx], industry: v }; setPrevIndustryYears(u); }} onCreateOption={(val) => { const opt = { label: val, value: val }; setCustomIndustryOpts((p) => [...p, opt]); const u = [...prevIndustryYears]; u[idx] = { ...u[idx], industry: opt }; setPrevIndustryYears(u); }} placeholder="e.g. Healthcare" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] font-medium text-[#94A3B8] font-dmsans mb-0.5 block">Years of experience</label>
                                                        <CreatableSelect options={industryYearsOptions} value={entry.years} onChange={(v) => { const u = [...prevIndustryYears]; u[idx] = { ...u[idx], years: v }; setPrevIndustryYears(u); }} placeholder="e.g. 3 years" styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-left">
                                        <div className="sm:col-span-2">
                                            <ChipField label="Target job titles" chips={jobTitles.map((t) => t.label)} />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <ChipField label="Top skills" chips={Array.isArray(user?.skills) ? user.skills : []} />
                                        </div>
                                        <FieldRow label="Years of experience" value={
                                            (() => {
                                                const y = user?.experienceYears;
                                                if (!y && y !== 0) return null;
                                                if (y <= 2) return '0–2 years';
                                                if (y <= 5) return '3–5 years';
                                                if (y <= 8) return '6–8 years';
                                                return '9+ years';
                                            })()
                                        } />
                                        <FieldRow label="Work mode" value={
                                            user?.remotePreference
                                                ? String(user.remotePreference)
                                                    .split(',')
                                                    .map((s) => s.trim())
                                                    .filter(Boolean)
                                                    .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
                                                    .join(', ')
                                                : null
                                        } />
                                        <FieldRow label="Work location" value={user?.locationPreference || null} />
                                        <FieldRow label="Salary expectation" value={user?.salaryExpectation ? `$${Number(user.salaryExpectation).toLocaleString()}` : null} />
                                        <ChipField label="Preferred industries" chips={prefIndustries.map((i) => i.label)} />
                                        <ChipField label="Preferred department" chips={prefDept ? [prefDept.label] : []} />
                                        {prevIndustryYears.some((e) => e.industry || e.years) && (
                                            <div className="sm:col-span-2">
                                                <Eyebrow>Experience context</Eyebrow>
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {prevIndustryYears.filter((e) => e.industry || e.years).map((e, i) => (
                                                        <div key={i} className="flex items-center gap-1.5">
                                                            {e.industry && (
                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold font-dmsans"
                                                                    style={{ background: '#F1F5F9', color: '#334155' }}>
                                                                    {e.industry.label}
                                                                </span>
                                                            )}
                                                            {e.industry && e.years && (
                                                                <span className="text-[10px] text-[#CBD5E1]">·</span>
                                                            )}
                                                            {e.years && (
                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold font-dmsans"
                                                                    style={{ background: '#EEF1FE', color: '#3F57E0' }}>
                                                                    {e.years.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>

                        </div>

                        {/* ══ RIGHT SIDEBAR ════════════════════════════════════════ */}
                        <div className="hidden lg:flex flex-col gap-[14px] w-[300px] flex-shrink-0">

                            {/* ── Resume / CV ──────────────────────────────────── */}
                            <Card className="overflow-hidden">
                                <CardHead title="Resume / CV" subtitle="PDF, DOC or DOCX · max 5 MB" />

                                <div className="p-4">
                                    <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleResumeUpload} />

                                    {user.resumeUpload?.uploadedAt ? (
                                        <>
                                            {/* File tile */}
                                            <div className="flex items-center gap-3 p-3 rounded-xl border border-[#E6E8EE]" style={{ background: '#FAFBFD' }}>
                                                {/* PDF icon */}
                                                <div className="w-10 h-12 rounded-lg flex flex-col items-center justify-center flex-shrink-0 gap-0.5"
                                                    style={{ background: 'linear-gradient(160deg,#FEE2E2 0%,#FECACA 100%)', border: '1px solid #FECACA' }}>
                                                    <span className="text-[8.5px] font-extrabold tracking-wide" style={{ color: '#DC2626' }}>PDF</span>
                                                    <FileText size={13} style={{ color: '#DC2626' }} />
                                                </div>
                                                <div className="flex-1 min-w-0 text-left">
                                                    <div className="text-[12.5px] font-bold text-[#0F172A] font-dmsans truncate leading-tight">
                                                        {user.resumeUpload.fileName || 'resume.pdf'}
                                                    </div>
                                                    <div className="text-[11px] text-[#94A3B8] font-dmsans mt-0.5">
                                                        {fmtBytes(user.resumeUpload.fileSize)} · Updated {formatDistanceToNow(new Date(user.resumeUpload.uploadedAt), { addSuffix: true })}
                                                    </div>
                                                </div>
                                            </div>

                                            {resumeMsg && (
                                                <p className="text-[11px] font-dmsans mt-2" style={{ color: resumeMsg.includes('fail') || resumeMsg.includes('fail') ? '#EF4444' : '#64748B' }}>
                                                    {resumeMsg}
                                                </p>
                                            )}

                                            {/* Action buttons */}
                                            <div className="grid grid-cols-2 gap-1.5 mt-3">
                                                <BtnSec onClick={() => resumeRef.current?.click()} disabled={resumeLoading} className="w-full justify-center">
                                                    {resumeLoading ? 'Uploading…' : 'Replace'}
                                                </BtnSec>
                                                <BtnSec onClick={handleDownloadResume} className="w-full justify-center">
                                                    <Download size={11} /> Download
                                                </BtnSec>
                                            </div>
                                            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => showToast('Coming soon')}
                                                    className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11.5px] font-semibold font-dmsans transition-colors"
                                                    style={{ border: '1px solid #C7D0F8', background: 'linear-gradient(180deg,#F4F6FF 0%,#E9EDFE 100%)', color: '#3F57E0' }}
                                                >
                                                    Check ATS Score
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setDelTarget({ type: 'resume' }); setDelOpen(true); }}
                                                    className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11.5px] font-semibold font-dmsans border bg-white transition-colors hover:bg-red-50"
                                                    style={{ borderColor: '#FECDCA', color: '#B42318' }}
                                                >
                                                    <Trash2 size={11} /> Delete
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* Empty drop-zone */}
                                            <button
                                                type="button"
                                                onClick={() => resumeRef.current?.click()}
                                                disabled={resumeLoading}
                                                className="w-full rounded-xl border-2 border-dashed border-[#C7D0F8] p-5 flex flex-col items-center gap-2.5 transition-colors hover:border-[#3F57E0] hover:bg-[#F4F6FF] group disabled:opacity-60"
                                                style={{ background: '#F8F9FF' }}
                                            >
                                                <div
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform"
                                                    style={{ background: '#EEF1FE' }}
                                                >
                                                    <FileText size={18} style={{ color: '#3F57E0' }} />
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-[13px] font-semibold text-[#0F172A] font-dmsans leading-tight">
                                                        {resumeLoading ? 'Uploading…' : 'Upload your CV'}
                                                    </p>
                                                    <p className="text-[11px] text-[#94A3B8] font-dmsans mt-0.5">
                                                        Click to browse · PDF, DOC, DOCX
                                                    </p>
                                                </div>
                                            </button>

                                            {resumeMsg && (
                                                <p className="text-[11px] font-dmsans mt-2 text-center" style={{ color: '#EF4444' }}>
                                                    {resumeMsg}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Card>

                            {/* ── Connected Accounts ───────────────────────────── */}
                            <Card className="p-4">
                                <Eyebrow>Connected accounts</Eyebrow>
                                <CardDivider />
                                {[
                                    {
                                        name: 'LinkedIn',
                                        status: linkedInConnected ? 'Connected' : 'Not connected',
                                        connected: linkedInConnected,
                                        icon: (
                                            <svg viewBox="0 0 24 24" width="14" height="14" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                        ),
                                    },
                                    {
                                        name: 'Google',
                                        status: googleConnected ? (user.email || 'Connected') : 'Connect to send email as you',
                                        connected: googleConnected,
                                        icon: (
                                            <svg viewBox="0 0 24 24" width="14" height="14"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                                        ),
                                    },
                                    {
                                        name: 'Calendar',
                                        status: 'Connect to schedule meetings',
                                        connected: false,
                                        icon: (
                                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                        ),
                                    },
                                ].map((account, i, arr) => (
                                    <div
                                        key={account.name}
                                        className="flex items-center gap-2.5 py-2"
                                        style={{ borderBottom: i < arr.length - 1 ? '1px solid #EFF1F5' : 'none' }}
                                    >
                                        <div className="w-[30px] h-[30px] rounded-lg bg-[#F1F3F7] flex items-center justify-center flex-shrink-0">
                                            {account.icon}
                                        </div>
                                        <div className="flex-1 min-w-0 text-left">
                                            <div className="text-[12.5px] font-semibold text-[#0F172A] font-dmsans">{account.name}</div>
                                            <div className="text-[11px] text-[#94A3B8] font-dmsans truncate">{account.status}</div>
                                        </div>
                                        {account.connected ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold font-dmsans"
                                                style={{ background: '#ECFDF5', color: '#10B981' }}>
                                                Connected
                                            </span>
                                        ) : (
                                            <BtnSec onClick={() => account.name === 'Google' ? handleConnectGoogle() : showToast('Coming soon')} className="flex-shrink-0 text-[11px]">Connect</BtnSec>
                                        )}
                                    </div>
                                ))}
                            </Card>

                            {/* ── Security ─────────────────────────────────────── */}
                            <Card className="p-4">
                                <div className="flex items-center gap-2">
                                    <Eyebrow>Security</Eyebrow>
                                </div>
                                <CardDivider />

                                {/* Password row */}
                                <div className="flex items-center gap-2.5 py-1">
                                    <div className="w-[30px] h-[30px] rounded-lg bg-[#F1F3F7] flex items-center justify-center flex-shrink-0">
                                        <Lock size={13} className="text-[#334155]" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <div className="text-[12.5px] font-semibold text-[#0F172A] font-dmsans">Password</div>
                                        <div className="text-[11px] text-[#94A3B8] font-dmsans">
                                            {hasPassword ? 'Keep your account secure' : 'Signed in with Google or LinkedIn — no password set'}
                                        </div>
                                    </div>
                                    {hasPassword ? (
                                    <BtnSec onClick={() => setPwdOpen((v) => !v)}>
                                        {pwdOpen ? 'Close' : 'Change'}
                                    </BtnSec>
                                    ) : (
                                    <BtnSec onClick={() => setSetpwdOpen((v) => !v)}>
                                        {setpwdOpen ? 'Close' : 'Set password'}
                                    </BtnSec>
                                    )}
                                </div>

                                {!hasPassword && setpwdOpen && (
                                    <>
                                        <CardDivider />
                                        <div className="flex flex-col gap-2.5 mt-1">
                                            {/* new password */}
                                            <div>
                                                <label className={labelCls}>New password</label>
                                                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-[#E6E8EE] bg-white">
                                                    <Lock size={12} className="text-[#94A3B8] flex-shrink-0" />
                                                    <input type={showSetpwd.next ? 'text' : 'password'} value={setpwd.next} onChange={(e) => setSetpwd({ ...setpwd, next: e.target.value })} placeholder="At least 8 characters"
                                                        className="flex-1 border-0 outline-none text-[12.5px] font-dmsans text-[#0F172A] bg-transparent placeholder:text-[#94A3B8] w-0" />
                                                    <button type="button" onClick={() => setShowSetpwd(p => ({ ...p, next: !p.next }))} className="text-[#94A3B8] hover:text-[#64748B] flex-shrink-0">
                                                        {showSetpwd.next ? <EyeOff size={12} /> : <Eye size={12} />}
                                                    </button>
                                                </div>
                                                {setpwd.next && (() => {
                                                    const bars = pwdStrength(setpwd.next);
                                                    const score = bars.filter(Boolean).length;
                                                    const color = score <= 1 ? '#EF4444' : score === 2 ? '#F59E0B' : score === 3 ? '#3B82F6' : '#10B981';
                                                    const unmet = [
                                                        [bars[0], 'At least 8 characters'],
                                                        [bars[1], 'One uppercase letter'],
                                                        [bars[2], 'One number'],
                                                        [bars[3], 'One special character'],
                                                    ].filter(([f]) => !f);
                                                    return (
                                                        <>
                                                            <div className="flex gap-1 mt-1.5">
                                                                {bars.map((filled, i) => (
                                                                    <div key={i} className="flex-1 h-[3px] rounded-full"
                                                                        style={{ background: filled ? color : '#EFF1F5' }} />
                                                                ))}
                                                            </div>
                                                            {unmet.length > 0 && (
                                                                <div className="flex flex-col gap-0.5 mt-1.5">
                                                                    {unmet.map(([, label]) => (
                                                                        <p key={label} className="text-[10.5px] text-[#94A3B8] font-dmsans">· {label}</p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            {/* confirm */}
                                            <div>
                                                <label className={labelCls}>Confirm password</label>
                                                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-[#E6E8EE] bg-white">
                                                    <Lock size={12} className="text-[#94A3B8] flex-shrink-0" />
                                                    <input type={showSetpwd.confirm ? 'text' : 'password'} value={setpwd.confirm} onChange={(e) => setSetpwd({ ...setpwd, confirm: e.target.value })} placeholder="Repeat new password"
                                                        className="flex-1 border-0 outline-none text-[12.5px] font-dmsans text-[#0F172A] bg-transparent placeholder:text-[#94A3B8] w-0" />
                                                    <button type="button" onClick={() => setShowSetpwd(p => ({ ...p, confirm: !p.confirm }))} className="text-[#94A3B8] hover:text-[#64748B] flex-shrink-0">
                                                        {showSetpwd.confirm ? <EyeOff size={12} /> : <Eye size={12} />}
                                                    </button>
                                                </div>
                                            </div>

                                            {setpwdMsg.text && (
                                                <p className={`text-[11px] font-medium font-dmsans ${setpwdMsg.ok ? 'text-[#10B981]' : 'text-red-500'}`}>{setpwdMsg.text}</p>
                                            )}

                                            <BtnPrimary onClick={handleSetPassword} disabled={setpwdSaving || !setpwd.next || !setpwd.confirm} className="w-full">
                                                <Check size={12} /> {setpwdSaving ? 'Setting…' : 'Set password'}
                                            </BtnPrimary>
                                        </div>
                                    </>
                                )}

                                {hasPassword && pwdOpen && (
                                    <>
                                        <CardDivider />
                                        <div className="flex flex-col gap-2.5 mt-1">
                                            {/* current */}
                                            <div>
                                                <label className={labelCls}>Current password</label>
                                                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-[#E6E8EE] bg-white">
                                                    <Lock size={12} className="text-[#94A3B8] flex-shrink-0" />
                                                    <input type={showPwd.current ? 'text' : 'password'} value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} placeholder="Current password"
                                                        className="flex-1 border-0 outline-none text-[12.5px] font-dmsans text-[#0F172A] bg-transparent placeholder:text-[#94A3B8] w-0" />
                                                    <button type="button" onClick={() => setShowPwd(p => ({ ...p, current: !p.current }))} className="text-[#94A3B8] hover:text-[#64748B] flex-shrink-0">
                                                        {showPwd.current ? <EyeOff size={12} /> : <Eye size={12} />}
                                                    </button>
                                                </div>
                                            </div>
                                            {/* new */}
                                            <div>
                                                <label className={labelCls}>New password</label>
                                                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-[#E6E8EE] bg-white">
                                                    <Lock size={12} className="text-[#94A3B8] flex-shrink-0" />
                                                    <input type={showPwd.next ? 'text' : 'password'} value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} placeholder="At least 8 characters"
                                                        className="flex-1 border-0 outline-none text-[12.5px] font-dmsans text-[#0F172A] bg-transparent placeholder:text-[#94A3B8] w-0" />
                                                    <button type="button" onClick={() => setShowPwd(p => ({ ...p, next: !p.next }))} className="text-[#94A3B8] hover:text-[#64748B] flex-shrink-0">
                                                        {showPwd.next ? <EyeOff size={12} /> : <Eye size={12} />}
                                                    </button>
                                                </div>
                                                {/* strength bars */}
                                                {pwd.next && (
                                                    <>
                                                        <div className="flex gap-1 mt-1.5">
                                                            {pwdBars.map((filled, i) => (
                                                                <div key={i} className="flex-1 h-[3px] rounded-full"
                                                                    style={{ background: filled ? pwdStrengthColor : '#EFF1F5' }} />
                                                            ))}
                                                        </div>
                                                        {[
                                                            [pwdBars[0], 'At least 8 characters'],
                                                            [pwdBars[1], 'One uppercase letter'],
                                                            [pwdBars[2], 'One number'],
                                                            [pwdBars[3], 'One special character'],
                                                        ].filter(([filled]) => !filled).length > 0 && (
                                                            <div className="flex flex-col gap-0.5 mt-1.5">
                                                                {[
                                                                    [pwdBars[0], 'At least 8 characters'],
                                                                    [pwdBars[1], 'One uppercase letter'],
                                                                    [pwdBars[2], 'One number'],
                                                                    [pwdBars[3], 'One special character'],
                                                                ].filter(([filled]) => !filled).map(([, label]) => (
                                                                    <p key={label} className="text-[10.5px] text-[#94A3B8] font-dmsans">· {label}</p>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {/* confirm */}
                                            <div>
                                                <label className={labelCls}>Confirm new password</label>
                                                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-[#E6E8EE] bg-white">
                                                    <Lock size={12} className="text-[#94A3B8] flex-shrink-0" />
                                                    <input type={showPwd.confirm ? 'text' : 'password'} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} placeholder="Repeat new password"
                                                        className="flex-1 border-0 outline-none text-[12.5px] font-dmsans text-[#0F172A] bg-transparent placeholder:text-[#94A3B8] w-0" />
                                                    <button type="button" onClick={() => setShowPwd(p => ({ ...p, confirm: !p.confirm }))} className="text-[#94A3B8] hover:text-[#64748B] flex-shrink-0">
                                                        {showPwd.confirm ? <EyeOff size={12} /> : <Eye size={12} />}
                                                    </button>
                                                </div>
                                            </div>

                                            <p className="text-[11px] text-[#64748B] font-dmsans leading-relaxed">
                                                Use 8+ characters with a mix of upper/lowercase, a number and a symbol.
                                            </p>

                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={signOutAll} onChange={(e) => setSignOutAll(e.target.checked)} className="w-3.5 h-3.5 accent-[#3F57E0]" />
                                                <span className="text-[11.5px] font-medium font-dmsans text-[#334155]">Sign out of all other devices</span>
                                            </label>

                                            {pwdMsg.text && (
                                                <p className={`text-[11px] font-medium font-dmsans ${pwdMsg.ok ? 'text-[#10B981]' : 'text-red-500'}`}>{pwdMsg.text}</p>
                                            )}

                                            <BtnPrimary onClick={handleChangePassword} disabled={pwdSaving || !pwd.current || !pwd.next || !pwd.confirm} className="w-full">
                                                <Check size={12} /> {pwdSaving ? 'Updating…' : 'Update password'}
                                            </BtnPrimary>
                                        </div>
                                    </>
                                )}
                            </Card>

                            {/* ── Subscription ─────────────────────────────────── */}
                            <Card className="overflow-hidden">
                                {/* coloured header band */}
                                <div
                                    className="px-4 py-3 flex items-center justify-between"
                                    style={{
                                        background: isFreeTier
                                            ? 'linear-gradient(135deg,#1F2A56 0%,#3F57E0 100%)'
                                            : 'linear-gradient(135deg,#065F46 0%,#10B981 100%)',
                                    }}
                                >
                                    <div className="text-left">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/70 font-dmsans">Current plan</p>
                                        <p className="text-[15px] font-bold text-white font-dmsans leading-tight mt-0.5">
                                            {isFreeTier ? 'Free Trial' : `${(user.subscription?.tier || 'free').charAt(0).toUpperCase() + (user.subscription?.tier || 'free').slice(1)} Plan`}
                                        </p>
                                    </div>
                                    <div
                                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                                        style={{ background: 'rgba(255,255,255,0.15)' }}
                                    >
                                        <Sparkles size={16} className="text-white" />
                                    </div>
                                </div>

                                <div className="px-4 py-3 space-y-3">
                                    {/* Trial status */}
                                    {isFreeTier && (
                                        <div>
                                            {trialEnd ? (
                                                <>
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <span className="text-[11.5px] font-semibold font-dmsans"
                                                            style={{ color: trialExpired ? '#EF4444' : '#16A34A' }}>
                                                            {trialExpired ? 'Trial ended' : `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left`}
                                                        </span>
                                                        <span className="text-[10.5px] text-[#94A3B8] font-dmsans">
                                                            {trialExpired ? '0 / 7 days' : `${7 - trialDaysLeft} / 7 days used`}
                                                        </span>
                                                    </div>
                                                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#EEF1F5' }}>
                                                        <div
                                                            className="h-full rounded-full transition-all"
                                                            style={{
                                                                width: trialExpired ? '100%' : `${Math.round(((7 - trialDaysLeft) / 7) * 100)}%`,
                                                                background: trialExpired ? '#EF4444' : '#10B981',
                                                            }}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <p className="text-[11.5px] text-[#64748B] font-dmsans">No trial period set</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Credits balance row */}
                                    <div className="flex items-center justify-between py-2.5 px-3 rounded-xl" style={{ background: '#F8FAFC', border: '1px solid #E6E8EE' }}>
                                        <div className="text-left">
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-[#94A3B8] font-dmsans">Credits balance</p>
                                            <p className="text-[18px] font-extrabold text-[#0F172A] font-dmsans leading-tight mt-0.5">
                                                {user?.creditsBalance ?? 0}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/credits')}
                                            className="text-[11px] font-semibold font-dmsans text-[#3F57E0] hover:underline"
                                        >
                                            View usage →
                                        </button>
                                    </div>

                                    {/* Renew / cancel info for paid plans */}
                                    {!isFreeTier && renewDateStr && (
                                        <p className="text-[11.5px] text-[#64748B] font-dmsans">
                                            {user.subscription?.cancelAtPeriodEnd
                                                ? <span className="text-[#F59E0B] font-semibold">Cancels {renewTime}</span>
                                                : <>Renews <strong className="text-[#0F172A]">{renewDateStr}</strong></>
                                            }
                                        </p>
                                    )}

                                    {/* Primary CTA */}
                                    {isFreeTier ? (
                                        <button
                                            type="button"
                                            onClick={() => navigate('/subscription')}
                                            className="w-full py-2 rounded-xl text-[12.5px] font-bold font-dmsans text-white hover:opacity-90 transition-opacity"
                                            style={{ background: 'linear-gradient(135deg,#3F57E0,#6B7CF5)' }}
                                        >
                                            Upgrade to Pro →
                                        </button>
                                    ) : (
                                        <BtnSec onClick={() => navigate('/subscription')} className="w-full justify-center">
                                            Manage subscription
                                        </BtnSec>
                                    )}

                                    {/* Cancel link for paid */}
                                    {!isFreeTier && !user.subscription?.cancelAtPeriodEnd && (
                                        <button
                                            type="button"
                                            onClick={() => { setDelTarget({ type: 'sub' }); setDelOpen(true); }}
                                            className="text-[11px] font-semibold font-dmsans text-red-400 hover:text-red-600 hover:underline w-full text-center transition-colors"
                                        >
                                            Cancel subscription
                                        </button>
                                    )}
                                </div>
                            </Card>

                            {/* ── Log out ──────────────────────────────────────── */}
                            <button
                                type="button"
                                onClick={() => setLogoutConfirm(true)}
                                className="w-full py-2 rounded-xl text-[12.5px] font-semibold font-dmsans border transition-colors hover:bg-red-50"
                                style={{ borderColor: '#FECDCA', color: '#B42318', background: '#fff' }}
                            >
                                Log out
                            </button>

                        </div>
                    </div>
                </div>
            </div>

            {/* ── view-only: work experience ─────────────────────────────────── */}
            {viewExp && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)' }} onClick={() => setViewExp(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ width: 'max-content', maxWidth: '80vw', maxHeight: '80vh', minWidth: 360 }} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 px-6 py-5 border-b border-[#EFF1F5] flex-shrink-0">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#EEF1FE,#C7D0F8)', color: '#3F57E0' }}>
                                <Briefcase size={18} />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                                <div className="text-[15px] font-bold text-[#0F172A] font-dmsans truncate">{viewExp.title}</div>
                                <div className="text-[12px] text-[#64748B] font-dmsans">{viewExp.company}</div>
                            </div>
                            <button onClick={() => setViewExp(null)} className="w-8 h-8 rounded-full flex items-center justify-center text-[#94A3B8] hover:bg-[#F4F5F8] hover:text-[#334155] transition-colors flex-shrink-0">
                                <X size={15} />
                            </button>
                        </div>
                        <div className="px-6 py-5 flex flex-col gap-4 text-left overflow-y-auto w-[560px] max-w-full">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Start date</p>
                                    <p className="text-[13px] font-medium text-[#334155]">{fmtMonthYear(viewExp.startingDate) || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">End date</p>
                                    <p className="text-[13px] font-medium text-[#334155]">{viewExp.stillWorking ? 'Present' : fmtMonthYear(viewExp.endingDate) || '—'}</p>
                                </div>
                            </div>
                            {viewExp.industry && (
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Industry</p>
                                    <p className="text-[13px] font-medium text-[#334155]">{viewExp.industry}</p>
                                </div>
                            )}
                            {viewExp.description && (
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Description</p>
                                    <p className="text-[13px] text-[#334155] leading-relaxed whitespace-pre-line">{viewExp.description}</p>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-[#EFF1F5] flex justify-end">
                            <button onClick={() => setViewExp(null)} className="px-4 py-2 text-[13px] font-semibold text-white rounded-xl hover:opacity-90 transition-opacity" style={{ backgroundColor: '#3F57E0' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── view-only: education ───────────────────────────────────────── */}
            {viewEdu && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)' }} onClick={() => setViewEdu(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ width: 'max-content', maxWidth: '80vw', maxHeight: '80vh', minWidth: 360 }} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 px-6 py-5 border-b border-[#EFF1F5] flex-shrink-0">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#EEF1FE,#C7D0F8)', color: '#3F57E0' }}>
                                <GraduationCap size={18} />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                                <div className="text-[15px] font-bold text-[#0F172A] font-dmsans truncate">{viewEdu.degree}{viewEdu.field ? ` · ${viewEdu.field}` : ''}</div>
                                <div className="text-[12px] text-[#64748B] font-dmsans">{viewEdu.school}</div>
                            </div>
                            <button onClick={() => setViewEdu(null)} className="w-8 h-8 rounded-full flex items-center justify-center text-[#94A3B8] hover:bg-[#F4F5F8] hover:text-[#334155] transition-colors flex-shrink-0">
                                <X size={15} />
                            </button>
                        </div>
                        <div className="px-6 py-5 flex flex-col gap-4 text-left overflow-y-auto w-[560px] max-w-full">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Start year</p>
                                    <p className="text-[13px] font-medium text-[#334155]">{viewEdu.startingYear || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">End year</p>
                                    <p className="text-[13px] font-medium text-[#334155]">{viewEdu.endingYear || '—'}</p>
                                </div>
                            </div>
                            {viewEdu.grade && (
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Grade / GPA</p>
                                    <p className="text-[13px] font-medium text-[#334155]">{viewEdu.grade}</p>
                                </div>
                            )}
                            {viewEdu.description && (
                                <div>
                                    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Description</p>
                                    <p className="text-[13px] text-[#334155] leading-relaxed whitespace-pre-line">{viewEdu.description}</p>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-[#EFF1F5] flex justify-end">
                            <button onClick={() => setViewEdu(null)} className="px-4 py-2 text-[13px] font-semibold text-white rounded-xl hover:opacity-90 transition-opacity" style={{ backgroundColor: '#3F57E0' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── modals ─────────────────────────────────────────────────────── */}
            <AddExpModal
                isOpen={expOpen}
                onClose={() => setExpOpen(false)}
                initialData={expEditData}
                index={expEditIdx}
                onDelete={expEditIdx !== null ? () => { setExpOpen(false); setDelTarget({ type: 'exp', index: expEditIdx }); setDelOpen(true); } : undefined}
            />
            <AddEduModal
                isOpen={eduOpen}
                onClose={() => setEduOpen(false)}
                initialData={eduEditData}
                index={eduEditIdx}
                onDelete={eduEditIdx !== null ? () => { setEduOpen(false); setDelTarget({ type: 'edu', index: eduEditIdx }); setDelOpen(true); } : undefined}
            />

            <DeleteConfirmModal
                isOpen={delOpen}
                onClose={() => { setDelOpen(false); setDelTarget(null); }}
                confirmHeader={
                    delTarget?.type === 'sub' ? 'Cancel Subscription'
                    : delTarget?.type === 'resume' ? 'Delete CV'
                    : 'Delete Entry'
                }
                confirmText={
                    delTarget?.type === 'sub' ? 'Are you sure you want to cancel your subscription?'
                    : delTarget?.type === 'resume' ? 'Are you sure you want to delete your saved CV?'
                    : 'Are you sure you want to delete this entry?'
                }
                onConfirm={confirmDelete}
            />
            {/* ── Logout confirmation modal ───────────────────────────────── */}
            {logoutConfirm && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6" style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)' }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-left">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#FEF2F2' }}>
                            <Lock size={18} className="text-red-500" />
                        </div>
                        <h3 className="text-[16px] font-bold text-[#0F172A] font-dmsans mb-1">Log out?</h3>
                        <p className="text-[13px] text-[#64748B] font-dmsans leading-relaxed mb-6">You'll be signed out of your Career Champ account on this device.</p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setLogoutConfirm(false)}
                                className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-[#334155] border border-[#E6E8EE] rounded-xl bg-white hover:bg-[#F6F7FB] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => { setLogoutConfirm(false); dispatch(logout()); navigate('/login'); }}
                                className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-white rounded-xl bg-red-500 hover:bg-red-600 transition-colors"
                            >
                                Log out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            </div>{/* end flex flex-1 min-h-0 pt-16 */}

            {/* ── Toast ──────────────────────────────────────────────────── */}
            {toast.msg && (
                <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-lg text-[13px] font-semibold font-dmsans text-white pointer-events-none"
                    style={{ background: toast.type === 'success' ? '#10B981' : '#0F172A' }}>
                    {toast.type === 'success' && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                    {toast.msg}
                </div>
            )}
        </div>
    );
};

export default Profile;
